import Decimal from 'decimal.js';
import { compact, isEmpty, uniq } from 'lodash';

import { AIOnboardingModalEntityField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { AiOnboardingModal_HouseholdFragment } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  getKgV2NodeIDFromSuggestion,
  isKGV2EntityPrincipalNodeType,
  type KGV2NodeForGrouping,
  resolvePrincipalsFromNodeMap,
} from '@/modules/aiSuggestions/kgV2Resolution.utils';
import { BASIC_ASSETS_SUBFORM } from '@/modules/entities/BasicAssetsSubform/BasicAssetsSubform.types';
import {
  BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE,
  BusinessEntityDetailsSubformType,
} from '@/modules/entities/BusinessEntityDetailsSubform/BusinessEntityDetailsSubform.types';
import { ALLOW_MULTIPLE_PRINCIPALS_TYPES_WITH_BUSINESS_ENTITIES } from '@/modules/entities/entities.constants';
import {
  CREATE_ENTITY_SHORT_FORM_NAMESPACE,
  EntityShortFormFields,
  EntityShortFormShape,
  PrincipalsOwnershipMap,
} from '@/modules/entities/EntityShortFormModal/CreateEntityShortForm/CreateEntityShortForm.types';
import { makeCreateEntityInput } from '@/modules/entities/EntityShortFormModal/CreateEntityShortForm/formUtils/CreateEntityShortForm.entityUtils';
import { getTaxStatusValuesFromEntityType } from '@/modules/entities/TaxStatusSubform/TaxStatusSubform.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import {
  AugmentedCreateEntityInput,
  EntityAttributionSource,
} from '@/types/schema';
import { getPulidKind, PulidKind } from '@/utils/pulid';
import { sentenceCaseWithAcronyms } from '@/utils/textFormatting';

///////////////////////////////////////////////////////////
// SUGGESTION DATA -> FORM DATA
///////////////////////////////////////////////////////////

type EntityDescriptionInputKey =
  | 'withCcorpBusinessEntity'
  | 'withCltTrust'
  | 'withCrtTrust'
  | 'withCustodialPersonalAccount'
  | 'withDonorAdvisedFund'
  | 'withGpBusinessEntity'
  | 'withGratTrust'
  | 'withIlitTrust'
  | 'withIndividualPersonalAccount'
  | 'withInsurancePersonalAccount'
  | 'withIrrevocableTrust'
  | 'withJointPersonalAccount'
  | 'withLlcBusinessEntity'
  | 'withLpBusinessEntity'
  | 'withPrivateFoundation'
  | 'withQprtTrust'
  | 'withQualifiedTuitionPersonalAccount'
  | 'withRetirementPersonalAccount'
  | 'withRevocableTrust'
  | 'withScorpBusinessEntity'
  | 'withSlatTrust'
  | 'withSoleProprietorshipBusinessEntity';

type EntityCreateInputWithDescription = {
  create: {
    description?: string | null;
  };
};

const ENTITY_DESCRIPTION_INPUT_KEYS: readonly EntityDescriptionInputKey[] = [
  'withCcorpBusinessEntity',
  'withCltTrust',
  'withCrtTrust',
  'withCustodialPersonalAccount',
  'withDonorAdvisedFund',
  'withGpBusinessEntity',
  'withGratTrust',
  'withIlitTrust',
  'withIndividualPersonalAccount',
  'withInsurancePersonalAccount',
  'withIrrevocableTrust',
  'withJointPersonalAccount',
  'withLlcBusinessEntity',
  'withLpBusinessEntity',
  'withPrivateFoundation',
  'withQprtTrust',
  'withQualifiedTuitionPersonalAccount',
  'withRetirementPersonalAccount',
  'withRevocableTrust',
  'withScorpBusinessEntity',
  'withSlatTrust',
  'withSoleProprietorshipBusinessEntity',
];

function withSuggestionNotesDescription(
  input: AugmentedCreateEntityInput,
  notes?: string | null
): AugmentedCreateEntityInput {
  if (!notes) {
    return input;
  }

  const inputWithDescriptions = input as AugmentedCreateEntityInput &
    Partial<
      Record<EntityDescriptionInputKey, EntityCreateInputWithDescription>
    >;

  ENTITY_DESCRIPTION_INPUT_KEYS.forEach((key) => {
    const subtypeInput = inputWithDescriptions[key];
    if (!subtypeInput) {
      return;
    }

    subtypeInput.create = {
      ...subtypeInput.create,
      description: notes,
    };
  });

  return input;
}

/**
 * Maps the ONBOARDING_ENTITY suggestion to form data.
 *
 * @param suggestions
 * @param principalNodesById
 */
export function mapEntitiesToFormData(
  suggestions: AiSuggestionForReviewCard[],
  principalNodesById: Map<string, KGV2NodeForGrouping> = new Map()
): AIOnboardingModalEntityField[] {
  // Suggestions in this list become form rows in the modal. We use this to gate
  // pending owner-entity suggestion IDs so we only pre-select ones that have a
  // matching row option in the dropdown (avoiding empty multi-select chips).
  const formRowSuggestionIDs = new Set(suggestions.map((s) => s.id));

  return compact(
    suggestions.map((s) => {
      if (!s.onboardingEntity) return null;

      const resolvedPrincipals = resolvePrincipalsFromNodeMap(
        s.onboardingEntity.principalNodeIDs ?? [],
        principalNodesById
      );

      // Keep resolved records and pending owner suggestions that have a sibling
      // form row. Other unresolved principals stay available through the Add
      // options until the user creates the record.
      const suggestedPrincipalIds = uniq(
        compact(
          resolvedPrincipals.map((principal) => {
            if (principal.resolvedId) {
              return principal.resolvedId;
            }

            if (
              isKGV2EntityPrincipalNodeType(principal.nodeType) &&
              principal.referenceSuggestionID &&
              formRowSuggestionIDs.has(principal.referenceSuggestionID)
            ) {
              return principal.referenceSuggestionID;
            }

            return null;
          })
        )
      );

      const selectedSinglePrincipalId = getFirstResolvedPrincipalId(
        suggestedPrincipalIds
      );

      return {
        _suggestionID: s.id,
        entityName:
          s.onboardingEntity?.displayName ??
          sentenceCaseWithAcronyms(s.onboardingEntity?.entityName || ''),
        entityType: s.onboardingEntity?.entityKind
          ? getEntityTypeFromEntityKind(s.onboardingEntity?.entityKind)
          : '',
        integrationEntityIds: [],
        // NOTE: We are populating both the multi-select and single-select fields
        // here, because the user might switch entity types. We want to at least
        // show the initially suggested principal(s).
        principalIds: suggestedPrincipalIds,
        principalId: selectedSinglePrincipalId,
        assetCategoryId: '',
        currentMarketValue: null,
        dateOfValuation: new Date(),
      };
    })
  );
}

export function getEmptyEntityField(): AIOnboardingModalEntityField {
  return {
    _suggestionID: '',
    entityName: '',
    entityType: '',
    principalIds: [],
    principalId: '',
    integrationEntityIds: [],
    assetCategoryId: '',
    currentMarketValue: null,
    dateOfValuation: new Date(),
  };
}

export function getMissingRequiredEntityFields(
  entity: AIOnboardingModalEntityField
): string[] {
  return compact([
    !entity.entityName && 'an entity name',
    !entity.entityType && 'an entity type',
    entity.principalIds.length === 0 &&
      !entity.principalId &&
      'a valid grantor, owner, or donor',
  ]);
}

export function hasValidPrincipalSelection(
  value: unknown,
  validPrincipalIDs: ReadonlySet<string>
): boolean {
  const selectedPrincipalIDs: unknown[] = Array.isArray(value)
    ? value
    : [value];
  return selectedPrincipalIDs.some(
    (principalID) =>
      typeof principalID === 'string' && validPrincipalIDs.has(principalID)
  );
}

function getFirstResolvedPrincipalId(suggestedPrincipalIds: string[]): string {
  // The single-select field can only create relationships to records that
  // already exist. Pending AI suggestion IDs remain in principalIds so bulk
  // business entity creation can preserve suggested owner dependencies.
  return (
    suggestedPrincipalIds.find((id) => {
      const pulidKind = getPulidKind(id);
      return (
        pulidKind === PulidKind.Entity || pulidKind === PulidKind.ClientProfile
      );
    }) || ''
  );
}

///////////////////////////////////////////////////////////
// FORM DATA -> MUTATION INPUT
///////////////////////////////////////////////////////////

/**
 * Converts the form data to the input data for the mutation.
 *
 * @param entities
 * @param household
 * @param suggestionsByID
 */
export function mapEntitiesToInput(
  entities: AIOnboardingModalEntityField[],
  household: AiOnboardingModal_HouseholdFragment,
  suggestionsByID: Record<string, AiSuggestionForReviewCard>
): AugmentedCreateEntityInput[] {
  return compact(
    entities.map((e) => {
      const shortFormInput = adaptEntityInputToEntityShortFormShape(e);
      if (!shortFormInput) {
        return null;
      }

      const createInput = makeCreateEntityInput(shortFormInput, household.id, {
        associatedDocumentIds: [],
      });

      const suggestion = e._suggestionID
        ? suggestionsByID[e._suggestionID]
        : undefined;
      const kgV2NodeID = getKgV2NodeIDFromSuggestion(suggestion);

      return withSuggestionNotesDescription(
        {
          ...createInput,
          create: {
            ...createInput.create,
            integrationEntityIDs: e.integrationEntityIds,
            onboardingEntitySuggestionIDs: e._suggestionID
              ? [e._suggestionID]
              : undefined,
            // Business entities can be owned by other entities. However, since we're
            // batch-onboarding entities, we don't have the entity IDs yet. So, we pass
            // the suggestion IDs of the owning entities for now, and the backend will
            // resolve them in the mutation resolver.
            owningOnboardingEntitySuggestionIDs: getPulidsOfKind(
              // We only care about the principalIds field here, because business
              // entities are all multi-principal entities.
              e.principalIds,
              PulidKind.AiSuggestion
            ),
            attributionSource: EntityAttributionSource.DocumentExtraction,
            kgV2NodeIDs: kgV2NodeID ? [kgV2NodeID] : undefined,
          },
        },
        suggestion?.onboardingEntity?.notes
      );
    })
  );
}

function adaptEntityInputToEntityShortFormShape(
  formInput: AIOnboardingModalEntityField
): EntityShortFormShape | null {
  const { entityName, entityType, principalIds, principalId } = formInput;
  if (!entityType) {
    throw new Error('Entity type is required');
  }

  if (entityType === 'grat') {
    return null;
  }

  const { estateInclusionStatus } =
    getTaxStatusValuesFromEntityType(entityType);

  const { principals, selectedSinglePrincipal, owners } =
    getClientProfilePrincipalsInput(entityType, principalIds, principalId);

  return {
    [CREATE_ENTITY_SHORT_FORM_NAMESPACE]: {
      name: entityName,
      entityType,
      principals,
      selectedSinglePrincipal,
      _ownershipSum: null,
      estateInclusionStatus,
      stateInEstateStatus: null,
      defaultDocumentId: undefined,
    },
    [BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE]: {
      keyPeople: [],
      keyPeopleNotes: '',
      owners,
      ownershipAsOfDate: null,
      _ownershipSum: null,
      _ownershipSyncIntegrationKind: null,
      // it's okay to default this to true; it's a no-op for unintegrated entities or entities that aren't connected to supported integrations
      syncOwnership: true,
    },
    [BASIC_ASSETS_SUBFORM]: formInput._showBasicAssetFields
      ? {
          basicAssets: {
            assetCategoryId: formInput.assetCategoryId,
            currentMarketValue: formInput.currentMarketValue,
            dateOfValuation: formInput.dateOfValuation,
            description: 'Default account',
            dirtyState: 'valuationFieldsDirty',
            integrationEntityIds: [],
            _previousIntegrationEntityIds: [],
            linkToAllAddeparEntities: false,
          },
        }
      : {},
  };
}

export function getClientProfilePrincipalsInput(
  entityType: EntityType,
  principalIds: string[],
  principalId: string
): Pick<EntityShortFormFields, 'principals' | 'selectedSinglePrincipal'> & {
  owners: BusinessEntityDetailsSubformType['owners'];
} {
  const allowMultiplePrincipals =
    ALLOW_MULTIPLE_PRINCIPALS_TYPES_WITH_BUSINESS_ENTITIES.includes(entityType);

  const validPrincipalIds = getPulidsOfKind(
    allowMultiplePrincipals ? principalIds : [principalId],
    PulidKind.ClientProfile
  );

  const validOwningEntityIds = getPulidsOfKind(
    allowMultiplePrincipals ? principalIds : [principalId],
    PulidKind.Entity
  );

  if (isEmpty(validPrincipalIds) && isEmpty(validOwningEntityIds)) {
    return {
      principals: {} as PrincipalsOwnershipMap,
      selectedSinglePrincipal: '',
      owners: [],
    };
  }

  const nonOwnerPrincipalsInput = allowMultiplePrincipals
    ? {
        principals: validPrincipalIds.reduce(
          (acc, id) => ({
            ...acc,
            [id]: {
              isIncluded: true,
              ownershipPercentage: null,
            },
          }),
          {} as PrincipalsOwnershipMap
        ),
        selectedSinglePrincipal: '',
      }
    : {
        principals: {} as PrincipalsOwnershipMap,
        selectedSinglePrincipal: validPrincipalIds[0] || '',
      };

  const businessOwners = [...validPrincipalIds, ...validOwningEntityIds];
  const numOwners = businessOwners.length;

  return {
    ...nonOwnerPrincipalsInput,
    owners: businessOwners.map((id) => ({
      ownerId: id,
      // For Business Entities, default each owner to have an equal share.
      percentOwned: new Decimal(100).div(numOwners),
    })),
  };
}

/**
 * Given a list of strings (pulids), returns the ones that are of the given pulid kind.
 *
 * @param input
 * @param pulidKind
 */
function getPulidsOfKind(
  input: string[] | undefined | null,
  pulidKind: PulidKind
): string[] {
  return input ? uniq(input.filter((p) => getPulidKind(p) === pulidKind)) : [];
}
